import apiService from './api'
import type { PaginatedResponse } from '@/types/api'

export interface BugReportAttachment {
  id: number
  uuid: string
  bug_report_uuid: string
  file_path: string
  file_url?: string | null
  file_name: string
  file_size: number
  mime_type?: string | null
  uploaded_at?: string | null
  created_at?: string
  updated_at?: string
}

export interface BugReport {
  id: number
  uuid: string
  title: string
  description: string
  status: 'open' | 'scheduled' | 'in-progress' | 'on-hold' | 'fixed' | 'closed'
  priority: 'low' | 'medium' | 'high' | 'critical'
  category?: 'bug' | 'feature' | 'improvement' | 'other'
  browser?: string
  os?: string
  steps_to_reproduce?: string
  expected_behavior?: string
  actual_behavior?: string
  admin_notes?: string
  page_url?: string
  // Deprecated: kept for backwards compatibility
  attachment_path?: string
  attachment_url?: string
  // New: multiple attachments
  attachments?: BugReportAttachment[]
  reported_by?: number
  assigned_to?: number
  reporter?: {
    id: number
    name: string
    email: string
  }
  assignee?: {
    id: number
    name: string
    email: string
  }
  created_at?: string
  updated_at?: string
}

export interface CreateBugReportData {
  title: string
  description: string
  priority?: 'low' | 'medium' | 'high' | 'critical'
  category?: 'bug' | 'feature' | 'improvement' | 'other'
  browser?: string
  os?: string
  steps_to_reproduce?: string
  expected_behavior?: string
  actual_behavior?: string
  page_url?: string
  // Deprecated: kept for backwards compatibility
  attachment?: File
  // New: multiple attachments
  attachments?: File[]
}

export interface UpdateBugReportData {
  title?: string
  description?: string
  status?: 'open' | 'scheduled' | 'in-progress' | 'on-hold' | 'fixed' | 'closed'
  priority?: 'low' | 'medium' | 'high' | 'critical'
  category?: 'bug' | 'feature' | 'improvement' | 'other'
  browser?: string
  os?: string
  steps_to_reproduce?: string
  expected_behavior?: string
  actual_behavior?: string
  assigned_to?: number
  admin_notes?: string
  page_url?: string
}

/**
 * Query params for listing bug reports.
 *
 * Multi-value `status`, `priority`, and `category` are sent as comma-separated
 * query strings (OR within each dimension; AND across dimensions on the API).
 */
export interface BugReportFilters {
  search?: string
  status?: string | string[]
  priority?: string | string[]
  category?: string | string[]
  reported_by?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  page?: number
  per_page?: number
}

export interface BugReportsResponse {
  data: BugReport[]
  current_page: number
  last_page: number
  per_page: number
  total: number
}

export interface BugReportResponse {
  data: BugReport
  message: string
}

export interface BugReportStatistics {
  total: number
  by_status: Record<string, number>
  by_priority: Record<string, number>
  by_category: Record<string, number>
}

class BugReportService {
  private readonly endpoint = '/bug-reports'

  /**
   * Appends a single or multi-value filter to query params as one CSV entry.
   * Skips empty arrays and blank strings.
   */
  private appendFilterValue(params: URLSearchParams, key: string, value?: string | string[]): void {
    if (value === undefined || value === null) return
    if (Array.isArray(value)) {
      const normalized = value.map((item) => item.trim()).filter((item) => item.length > 0)
      if (normalized.length === 0) return
      params.append(key, normalized.join(','))
      return
    }
    const normalized = value.trim()
    if (normalized.length === 0) return
    params.append(key, normalized)
  }

  /**
   * Submit a new bug report
   */
  async createBugReport(data: CreateBugReportData | FormData): Promise<BugReportResponse> {
    // If FormData, Axios will automatically set Content-Type to multipart/form-data
    // If object, send as JSON
    return apiService.post<BugReportResponse>(this.endpoint, data)
  }

  /**
   * Get bug reports (admin only)
   */
  async getBugReports(filters?: BugReportFilters): Promise<PaginatedResponse<BugReport>> {
    const params = new URLSearchParams()
    
    if (filters) {
      if (filters.search) params.append('search', filters.search)
      this.appendFilterValue(params, 'status', filters.status)
      this.appendFilterValue(params, 'priority', filters.priority)
      this.appendFilterValue(params, 'category', filters.category)
      if (filters.reported_by) params.append('reported_by', filters.reported_by.toString())
      if (filters.sort_by) params.append('sort_by', filters.sort_by)
      if (filters.sort_order) params.append('sort_order', filters.sort_order)
      if (filters.page) params.append('page', filters.page.toString())
      if (filters.per_page) params.append('per_page', filters.per_page.toString())
    }

    const queryString = params.toString()
    const url = queryString ? `${this.endpoint}?${queryString}` : this.endpoint
    
    return apiService.getPaginated<BugReport>(url)
  }

  /**
   * Get bug reports for current user
   */
  async getMyBugReports(filters?: BugReportFilters): Promise<PaginatedResponse<BugReport>> {
    const params = new URLSearchParams()
    
    if (filters) {
      if (filters.search) params.append('search', filters.search)
      this.appendFilterValue(params, 'status', filters.status)
      this.appendFilterValue(params, 'priority', filters.priority)
      this.appendFilterValue(params, 'category', filters.category)
      if (filters.sort_by) params.append('sort_by', filters.sort_by)
      if (filters.sort_order) params.append('sort_order', filters.sort_order)
      if (filters.page) params.append('page', filters.page.toString())
      if (filters.per_page) params.append('per_page', filters.per_page.toString())
    }

    const queryString = params.toString()
    const url = queryString ? `${this.endpoint}/my-reports?${queryString}` : `${this.endpoint}/my-reports`
    
    return apiService.getPaginated<BugReport>(url)
  }

  /**
   * Get a single bug report by ID or UUID
   * Accepts either UUID string or numeric ID for backward compatibility
   */
  async getBugReport(idOrUuid: number | string): Promise<BugReportResponse> {
    return apiService.get<BugReportResponse>(`${this.endpoint}/${idOrUuid}`)
  }

  /**
   * Update a bug report (admin only)
   * Accepts either UUID string or numeric ID for backward compatibility
   */
  async updateBugReport(idOrUuid: number | string, data: UpdateBugReportData): Promise<BugReportResponse> {
    return apiService.put<BugReportResponse>(`${this.endpoint}/${idOrUuid}`, data)
  }

  /**
   * Update bug report status (admin only)
   * Accepts either UUID string or numeric ID for backward compatibility
   */
  async updateBugReportStatus(idOrUuid: number | string, status: BugReport['status'], adminNotes?: string): Promise<BugReportResponse> {
    return apiService.patch<BugReportResponse>(`${this.endpoint}/${idOrUuid}/status`, {
      status,
      admin_notes: adminNotes
    })
  }

  /**
   * Delete a bug report (admin only)
   * Accepts either UUID string or numeric ID for backward compatibility
   */
  async deleteBugReport(idOrUuid: number | string): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`${this.endpoint}/${idOrUuid}`)
  }

  /**
   * Get bug report statistics (admin only)
   */
  async getStatistics(): Promise<{ data: BugReportStatistics }> {
    return apiService.get<{ data: BugReportStatistics }>(`${this.endpoint}/statistics`)
  }

  /**
   * Download bug report attachment
   */
  async downloadAttachment(bugReportUuid: string): Promise<Blob> {
    const response = await apiService.get<Blob>(`${this.endpoint}/${bugReportUuid}/attachment/download`, {
      responseType: 'blob'
    })
    return response
  }
}

export const bugReportService = new BugReportService()
export default bugReportService

